import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BadgeCheck, Search, X } from "lucide-react";
import { STRATEGY_LIBRARY, loadLibrarySource, totalResult, type LibraryStrategy } from "@/lib/strategy-library";
import { providerLabel } from "@/lib/account";
import { cn } from "@/lib/utils";

const money = (n: number) =>
  `${n >= 0 ? "+" : "−"}$${Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: Math.abs(n) >= 100 ? 0 : 2 })}`;
const tone = (n: number) => (n >= 0 ? "var(--success)" : "var(--danger)");
const chip = (on: boolean) =>
  cn(
    "rounded-full border px-2.5 py-[3px] text-xs",
    on
      ? "border-[var(--primary)] bg-[var(--primary-soft)] text-[var(--primary)]"
      : "border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
  );
const builtFor = (s: LibraryStrategy) =>
  `${s.builtFor.symbol} · ${providerLabel(s.builtFor.provider)} ${s.builtFor.market === "futures" ? "Futures" : "Spot"} · ${s.builtFor.timeframe}`;

/**
 * Ready strategies to start a backtest from: cards on the left, the picked one's
 * story and results by year on the right. "Use this strategy" hands back its
 * script; the backtest then runs as any other, before anything can go live.
 */
export function StrategyLibraryDialog({
  onPick,
  onClose,
}: {
  onPick: (strategy: LibraryStrategy, source: string) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string>("All");
  const [selectedId, setSelectedId] = useState(STRATEGY_LIBRARY[0]!.id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  // Read at key time: callers pass a new function on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    panel.current?.querySelector<HTMLElement>("input")?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCloseRef.current();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of STRATEGY_LIBRARY) counts.set(s.category, (counts.get(s.category) || 0) + 1);
    return [["All", STRATEGY_LIBRARY.length] as const, ...counts];
  }, []);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return STRATEGY_LIBRARY.filter(
      (s) =>
        (category === "All" || s.category === category) &&
        (!needle || `${s.name} ${s.summary} ${s.builtFor.symbol} ${s.category}`.toLowerCase().includes(needle))
    );
  }, [q, category]);
  const selected = shown.find((s) => s.id === selectedId) ?? shown[0];

  async function use(s: LibraryStrategy) {
    setLoading(true);
    setError(null);
    try {
      onPick(s, await loadLibrarySource(s.id));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Couldn't load the script. Try again.");
      setLoading(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onMouseDown={onClose}>
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="library-title"
        className="flex h-[min(640px,calc(100dvh-2rem))] w-full max-w-[980px] flex-col overflow-hidden rounded-[16px] border border-[var(--border)] bg-[var(--bg-card)] shadow-lg"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-[var(--border)] px-5 py-4">
          <h2 id="library-title" className="m-0 text-base font-semibold text-[var(--text-primary)]">
            Strategy library
          </h2>
          <span className="text-xs text-[var(--text-muted)]">Ready strategies, checked on our engine</span>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="ml-auto rounded-md p-1 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
          <div className="flex min-h-0 min-w-0 flex-col gap-3 overflow-y-auto border-b border-[var(--border)] p-4 md:border-b-0 md:border-r">
            <label className="flex items-center gap-2 rounded-[10px] border border-[var(--border)] bg-[var(--bg-secondary)] px-2.5 py-1.5 text-[13px] text-[var(--text-disabled)] focus-within:border-[var(--primary)]">
              <Search size={14} className="shrink-0" />
              <input
                type="search"
                aria-label="Search the library"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search strategies or coins"
                className="!h-auto !min-h-0 w-full !border-0 !bg-transparent !p-0 !text-[13px] text-[var(--text-primary)] !shadow-none !outline-none placeholder:text-[var(--text-disabled)]"
              />
            </label>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Category">
              {categories.map(([c, n]) => (
                <button key={c} type="button" aria-pressed={category === c} onClick={() => setCategory(c)} className={chip(category === c)}>
                  {c} <span className="num opacity-70">{n}</span>
                </button>
              ))}
            </div>
            {shown.length === 0 ? (
              <div className="py-8 text-center text-[13px] text-[var(--text-muted)]">No strategy matches “{q}”.</div>
            ) : (
              <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(210px,1fr))]">
                {shown.map((s) => {
                  const t = totalResult(s);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      aria-pressed={selected?.id === s.id}
                      onClick={() => setSelectedId(s.id)}
                      className={cn(
                        "flex min-w-0 flex-col gap-1.5 rounded-[12px] border bg-[var(--bg-card)] p-3 text-left transition-colors hover:border-[var(--border-hover)]",
                        selected?.id === s.id ? "border-[var(--primary)]" : "border-[var(--border)]"
                      )}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-[13px] font-semibold text-[var(--text-primary)]">{s.name}</span>
                        <span className="shrink-0 rounded-md bg-[var(--bg-hover)] px-1.5 text-[11px] text-[var(--text-muted)]">{s.level}</span>
                      </span>
                      <span className="truncate text-[11.5px] text-[var(--text-muted)]">
                        {s.category} · {s.builtFor.symbol} {s.builtFor.timeframe}
                      </span>
                      <span className="flex items-center justify-between gap-2 text-[11.5px]">
                        <span className="text-[var(--text-muted)]">{s.results.length} years</span>
                        <span className="num font-semibold" style={{ color: tone(t.pnl) }}>
                          {money(t.pnl)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {selected && (
            <section aria-label={selected.name} className="flex min-h-0 min-w-0 flex-col gap-3 overflow-y-auto bg-[var(--bg-card-elevated)] p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">{selected.name}</h3>
                <span className="shrink-0 rounded-md bg-[var(--bg-hover)] px-1.5 text-[11px] text-[var(--text-muted)]">{selected.level}</span>
              </div>
              <p className="m-0 text-[13px] leading-relaxed text-[var(--text-secondary)]">{selected.summary}</p>
              <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12.5px]">
                <dt className="text-[var(--text-muted)]">Built for</dt>
                <dd className="m-0 text-[var(--text-primary)]">{builtFor(selected)}</dd>
                <dt className="text-[var(--text-muted)]">Results on</dt>
                <dd className="m-0 text-[var(--text-primary)]">{selected.capital}</dd>
                <dt className="text-[var(--text-muted)]">By</dt>
                <dd className="m-0 text-[var(--text-primary)]">{selected.author}</dd>
              </dl>

              <div>
                <div className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.06em] text-[var(--text-muted)]">Results by year</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {selected.results.map((r) => (
                    <div key={r.year} className="rounded-[8px] bg-[var(--bg-card)] px-2 py-1.5">
                      <div className="text-[10.5px] text-[var(--text-muted)]">{r.year === "2026" ? "2026 to Oct" : r.year}</div>
                      <div className="num text-[13px] font-semibold" style={{ color: tone(r.pnl) }}>
                        {money(r.pnl)}
                      </div>
                      <div className="num text-[10.5px] text-[var(--text-muted)]">
                        {r.won}/{r.trades} won · DD ${Math.round(r.maxDrawdown).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-1.5 flex items-center gap-1 text-[11px] text-[var(--primary)]">
                  <BadgeCheck size={13} aria-hidden />
                  Run on our engine, Binance candles, {selected.verifiedOn}
                </div>
              </div>

              {selected.caution && (
                <div className="rounded-[8px] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] px-2.5 py-2 text-[12px] text-[var(--warning)]">
                  {selected.caution}
                </div>
              )}
              <p className="m-0 text-[11px] text-[var(--text-muted)]">
                Past results don't promise future ones. You'll backtest it on your own settings before anything trades.
              </p>
              {error && <div className="text-xs text-[var(--danger)]">{error}</div>}
              <div className="mt-auto flex justify-end pt-1">
                <button type="button" className="btn btn-primary" disabled={loading} onClick={() => void use(selected)}>
                  {loading ? "Loading…" : "Use this strategy"}
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
